'use client';

import { createPortal } from 'react-dom';
import { useEffect, useId, useRef, useState } from 'react';

import { ReactComponent as QuestionIcon } from '@/public/svgs/global/Icons/Circle Question.svg';

import styles from './AccountFieldTooltip.module.scss';

type TooltipPosition = {
  top: number;
  left: number;
  placement: 'above' | 'below';
};

type AccountFieldTooltipProps = {
  tooltipText: string;
  toggleOnClick?: boolean;
};

const TOOLTIP_GAP = 8;
const VIEWPORT_GUTTER = 8;

export default function AccountFieldTooltip({ tooltipText, toggleOnClick = false }: AccountFieldTooltipProps): JSX.Element
{
  const tooltipId = `account-field-tooltip-${ useId().replace(/:/g, '') }`;
  const anchorRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const [isTooltipOpen, setIsTooltipOpen] = useState(false);
  const [position, setPosition] = useState<TooltipPosition | null>(null);

  useEffect(() =>
  {
    if (!isTooltipOpen)
    {
      setPosition(null);
      return;
    }

    function updatePosition(): void
    {
      const anchor = anchorRef.current?.getBoundingClientRect();
      const tooltip = tooltipRef.current?.getBoundingClientRect();
      if (!anchor || !tooltip)
        return;

      const placeBelow = anchor.top < tooltip.height + TOOLTIP_GAP + VIEWPORT_GUTTER;
      const centerX = Math.min(
        Math.max(anchor.left + (anchor.width / 2), (tooltip.width / 2) + VIEWPORT_GUTTER),
        window.innerWidth - (tooltip.width / 2) - VIEWPORT_GUTTER,
      );

      setPosition({
        top: window.scrollY + (placeBelow ? anchor.bottom + TOOLTIP_GAP : anchor.top - TOOLTIP_GAP),
        left: window.scrollX + centerX,
        placement: placeBelow ? 'below' : 'above',
      });
    }

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () =>
    {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isTooltipOpen, tooltipText]);

  const tooltip = isTooltipOpen && typeof document !== 'undefined'
    ? createPortal(
      <span
        ref={ tooltipRef }
        id={ tooltipId }
        className={ styles.tooltipContent }
        data-placement={ position?.placement ?? 'above' }
        role="tooltip"
        style={ { top: position?.top ?? 0, left: position?.left ?? 0, visibility: position ? 'visible' : 'hidden' } }>
        { tooltipText }
      </span>,
      document.body,
    )
    : null;

  return (
    <>
      <div ref={ anchorRef } className={ styles.tooltip } onMouseEnter={ () => { if (!toggleOnClick) setIsTooltipOpen(true); } } onMouseLeave={ () => { if (!toggleOnClick) setIsTooltipOpen(false); } }>
        <button
          type="button"
          className={ styles.tooltipIcon }
          aria-label={ tooltipText }
          aria-controls={ tooltipId }
          aria-expanded={ isTooltipOpen }
          aria-describedby={ isTooltipOpen ? tooltipId : undefined }
          onClick={ () => setIsTooltipOpen(current => toggleOnClick ? !current : true) }
          onFocus={ () => { if (!toggleOnClick) setIsTooltipOpen(true); } }
          onBlur={ () => setIsTooltipOpen(false) }
          onKeyDown={ (event) =>
          {
            if (event.key === 'Escape')
            {
              setIsTooltipOpen(false);
              event.currentTarget.blur();
            }
          } }>
          <QuestionIcon aria-hidden="true" />
        </button>
      </div>
      { tooltip }
    </>
  );
}
